"use client";

import { useEffect } from "react";
import { ArrowRight, Check, Phone, X } from "lucide-react";
import { IndustryLiveDemoSection } from "~/components/siteglue/industries/shared/industry-live-demo-section";
import {
  FadeIn,
  HomeEyebrow,
  HomeHeading,
  HomeLead,
  HomeSection,
} from "~/components/siteglue/home/section-shell";
import { trackSiteGlueEvent } from "~/lib/siteglue/cta-config";
import { INDUSTRY_LIVE_DEMO_PROJECT_IDS } from "~/lib/siteglue/industry-live-demo-projects";
import { SITEGLUE_COLORS } from "~/lib/siteglue/marketing-chrome";
import type { LiveDemoSectionConfig } from "~/components/siteglue/roles/shared/types";
import {
  BuildAiPhoneSystemForm,
  scrollToAiPhoneSystemBuildForm,
} from "./build-ai-phone-system-form";

const HOW_CALLS_WORK = [
  {
    title: "Answer the call",
    body: "Your SiteGlue Agent greets callers with your brand voice, confirms why they’re calling, and keeps the conversation moving.",
  },
  {
    title: "Understand the request",
    body: "It asks clarifying questions, looks up what it needs, and decides whether to answer, book, take a message, or route to a person.",
  },
  {
    title: "Take action",
    body: "Book appointments, capture messages, answer FAQs, or transfer to the right teammate’s mobile—with Human-in-the-Loop™ when needed.",
  },
  {
    title: "Keep your team in control",
    body: "Your people stay reachable on the phones they already use. The SiteGlue Agent handles volume; your team handles judgment calls.",
  },
] as const;

const COMPARISON_ROWS = [
  {
    label: "Answers after hours",
    traditional: "Voicemail or missed call",
    siteglue: "Live AI conversation anytime",
  },
  {
    label: "Books appointments",
    traditional: "Staff callback required",
    siteglue: "Books during the call",
  },
  {
    label: "Takes structured messages",
    traditional: "Scattered voicemails",
    siteglue: "Captured with context for follow-up",
  },
  {
    label: "Routes by intent",
    traditional: "Static menus / extensions",
    siteglue: "Natural-language routing",
  },
  {
    label: "Works across channels",
    traditional: "Phone only",
    siteglue: "Phone, web, text, and messaging",
  },
  {
    label: "Keeps humans in the loop",
    traditional: "All or nothing",
    siteglue: "Human-in-the-Loop™ by design",
  },
] as const;

const INDUSTRIES = [
  {
    title: "Home services",
    body: "Answer scheduling requests, capture job details, and route urgent calls without missing the next lead.",
  },
  {
    title: "Professional services",
    body: "Qualify inquiries, book consults, and keep intake moving when your front desk is already full.",
  },
  {
    title: "Healthcare practices",
    body: "Handle appointment requests, FAQs, and message taking—then connect patients to your team when needed.",
  },
  {
    title: "Multi-location teams",
    body: "Give every location a consistent AI Phone System that still routes to the right people on the ground.",
  },
] as const;

const HITL_POINTS = [
  "Intent-based routing to the right person",
  "Live transfer to team member mobile numbers",
  "Message taking when nobody can pick up",
  "Full context so your team isn’t starting from zero",
] as const;

const LIVE_DEMO_CONFIG: LiveDemoSectionConfig = {
  projectId: INDUSTRY_LIVE_DEMO_PROJECT_IDS.legal,
  demoAgentLabel: "Live SiteGlue Agent demo",
  testIdPrefix: "ai-phone-system",
  initialTab: "aiChat",
  hideOuterChrome: true,
  embedded: true,
  tabOrder: ["call", "aiChat", "sms", "email", "contact"],
};

export function AiPhoneSystemLandingPage() {
  useEffect(() => {
    trackSiteGlueEvent("solution_page_view", { page: "ai-phone-system" });
    trackSiteGlueEvent("ai_phone_system_page_view", {
      page: "ai-phone-system",
      page_path: typeof window !== "undefined" ? window.location.pathname : "/ai-phone-system",
      page_location: typeof window !== "undefined" ? window.location.href : undefined,
    });
  }, []);

  return (
    <div className="bg-white text-slate-900">
      {/* Hero — dark SiteGlue landing pattern, centered conversion */}
      <section className="relative isolate overflow-hidden bg-slate-950 text-white">
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden
          style={{
            background: `
              radial-gradient(ellipse 80% 60% at 70% 20%, ${SITEGLUE_COLORS.blue}55, transparent 55%),
              radial-gradient(ellipse 50% 40% at 15% 80%, ${SITEGLUE_COLORS.green}33, transparent 50%),
              linear-gradient(180deg, #0b1220 0%, #0f172a 55%, #111827 100%)
            `,
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          aria-hidden
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          }}
        />

        <div className="relative mx-auto flex min-h-[calc(100dvh-4rem)] max-w-3xl flex-col items-center justify-center px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8">
          <FadeIn className="w-full">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#9cbb5a]">
              AI Phone System
            </p>
            <h1 className="mt-4 text-balance text-[clamp(2.1rem,4vw+0.75rem,3.5rem)] font-bold leading-[1.08] tracking-tight text-white">
              The AI Phone System That Lives Online.
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-pretty text-lg font-medium leading-relaxed text-slate-200 sm:text-xl">
              Your agent answers calls, talks with website visitors, captures or schedules
              appointments when a calendar is connected, and routes people to your team.
            </p>
            <p className="mx-auto mt-4 text-sm font-medium text-slate-400 sm:text-base">
              No new phones. No new headcount. Always Human-in-the-Loop™.
            </p>

            <div className="mx-auto mt-8 w-full max-w-xl text-left">
              <BuildAiPhoneSystemForm
                id="build-ai-phone-system"
                location="hero"
                tone="dark"
                inputId="ai-phone-system-website-hero"
                className="mx-auto"
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Category challenge → multi-channel */}
      <HomeSection tone="white">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <HomeHeading className="mx-auto">Your Phone System Should Do More.</HomeHeading>
          <HomeLead className="mx-auto">
            SiteGlue doesn’t stop at phone calls. Your SiteGlue Agent can also respond to texts,
            emails, website visitors, and supported messaging channels—then book appointments, take
            messages, answer questions, or connect people to your team.
          </HomeLead>
        </FadeIn>

        <IndustryLiveDemoSection config={LIVE_DEMO_CONFIG} />
      </HomeSection>

      {/* How calls work */}
      <HomeSection id="how-calls-work" tone="slate">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <HomeHeading className="mx-auto">How Calls Work</HomeHeading>
          <HomeLead className="mx-auto">
            Inbound conversations your AI Phone System can handle from the first ring.
          </HomeLead>
        </FadeIn>
        <ol className="mt-12 grid gap-5 sm:grid-cols-2">
          {HOW_CALLS_WORK.map((step, index) => (
            <FadeIn key={step.title} delay={index * 0.05}>
              <li className="h-full rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm">
                <p
                  className="text-xs font-semibold uppercase tracking-[0.14em]"
                  style={{ color: SITEGLUE_COLORS.blue }}
                >
                  Step {index + 1}
                </p>
                <h3 className="mt-2 text-lg font-semibold text-slate-900">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.body}</p>
              </li>
            </FadeIn>
          ))}
        </ol>
      </HomeSection>

      {/* SiteGlue Agent vs auto-attendant */}
      <HomeSection tone="white">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <HomeHeading className="mx-auto">
            An SiteGlue Agent—Not Another Auto-Attendant
          </HomeHeading>
          <HomeLead className="mx-auto">
            Menus and extensions route calls. An SiteGlue Agent has the conversation, takes action, and
            only involves your team when a human should step in.
          </HomeLead>
        </FadeIn>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <FadeIn>
            <div className="h-full rounded-2xl border border-slate-200 bg-slate-50 p-6 text-left">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Traditional auto-attendant
              </p>
              <ul className="mt-4 space-y-3 text-sm text-slate-600">
                <li className="flex gap-2">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                  Press 1 for sales, press 2 for support
                </li>
                <li className="flex gap-2">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                  Dead ends after hours
                </li>
                <li className="flex gap-2">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden />
                  Can’t book, answer, or follow through
                </li>
              </ul>
            </div>
          </FadeIn>
          <FadeIn delay={0.06}>
            <div
              className="h-full rounded-2xl border p-6 text-left"
              style={{
                borderColor: "rgba(79,129,189,0.35)",
                background: "linear-gradient(180deg, rgba(79,129,189,0.08) 0%, #ffffff 55%)",
              }}
            >
              <p
                className="text-xs font-semibold uppercase tracking-[0.14em]"
                style={{ color: SITEGLUE_COLORS.blue }}
              >
                SiteGlue Agent
              </p>
              <ul className="mt-4 space-y-3 text-sm text-slate-700">
                <li className="flex gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                  Natural conversation from the first hello
                </li>
                <li className="flex gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                  Answers, books, messages, and routes by intent
                </li>
                <li className="flex gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                  Human-in-the-Loop™ when your team should take over
                </li>
              </ul>
            </div>
          </FadeIn>
        </div>
      </HomeSection>

      {/* Human-in-the-Loop */}
      <HomeSection tone="slate">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <HomeEyebrow>Human-in-the-Loop™</HomeEyebrow>
          <HomeHeading className="mx-auto">
            AI handles the work. Your team stays in control.
          </HomeHeading>
          <HomeLead className="mx-auto">
            SiteGlue isn’t built to replace your people. Your SiteGlue Agent takes the first
            conversation—then connects callers to the right teammate’s mobile when a live handoff is
            the right move.
          </HomeLead>
        </FadeIn>
        <ul className="mx-auto mt-10 grid max-w-3xl gap-3 text-left text-sm text-slate-700 sm:grid-cols-2">
          {HITL_POINTS.map((item) => (
            <li
              key={item}
              className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3"
            >
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
              {item}
            </li>
          ))}
        </ul>
      </HomeSection>

      {/* Comparison table */}
      <HomeSection id="compare" tone="white">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <HomeHeading className="mx-auto">Traditional Phone System vs SiteGlue</HomeHeading>
          <HomeLead className="mx-auto">
            Same business phone number. A different outcome for every inbound conversation.
          </HomeLead>
        </FadeIn>
        <div className="mt-10 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3 font-semibold text-slate-700 sm:px-6">Capability</th>
                <th className="px-4 py-3 font-semibold text-slate-500 sm:px-6">
                  Traditional phone system
                </th>
                <th
                  className="px-4 py-3 font-semibold sm:px-6"
                  style={{ color: SITEGLUE_COLORS.blue }}
                >
                  SiteGlue AI Phone System
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-3.5 font-medium text-slate-900 sm:px-6">{row.label}</td>
                  <td className="px-4 py-3.5 text-slate-500 sm:px-6">{row.traditional}</td>
                  <td className="px-4 py-3.5 font-medium text-slate-800 sm:px-6">{row.siteglue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </HomeSection>

      {/* Industries */}
      <HomeSection tone="slate">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <HomeHeading className="mx-auto">Built for Teams Who Can’t Miss Calls</HomeHeading>
          <HomeLead className="mx-auto">
            An AI Phone System that fits how your organization already works.
          </HomeLead>
        </FadeIn>
        <div className="mt-12 grid gap-5 sm:grid-cols-2">
          {INDUSTRIES.map((item, index) => (
            <FadeIn key={item.title} delay={index * 0.04}>
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 text-left shadow-sm">
                <h3 className="text-lg font-semibold text-slate-900">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </HomeSection>

      {/* Keep phones */}
      <HomeSection tone="white">
        <FadeIn className="mx-auto max-w-3xl text-center">
          <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-blue-50">
            <Phone className="h-5 w-5" style={{ color: SITEGLUE_COLORS.blue }} aria-hidden />
          </div>
          <HomeHeading className="mx-auto">Keep the Phones Your Team Already Uses</HomeHeading>
          <HomeLead className="mx-auto">
            No new desk phones. No rip-and-replace PBX project. Your SiteGlue Agent answers on your
            business number and transfers to the mobiles your team already carries.
          </HomeLead>
          <button
            type="button"
            className="mt-6 inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: SITEGLUE_COLORS.blue }}
            onClick={() => scrollToAiPhoneSystemBuildForm()}
          >
            Build yours
            <ArrowRight className="h-4 w-4" aria-hidden />
          </button>
        </FadeIn>
      </HomeSection>

      {/* Final CTA */}
      <HomeSection id="get-started" tone="brand" className="overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          aria-hidden
          style={{
            background: `
              radial-gradient(ellipse 70% 50% at 50% 0%, ${SITEGLUE_COLORS.blue}66, transparent 55%),
              radial-gradient(ellipse 40% 40% at 80% 80%, ${SITEGLUE_COLORS.green}44, transparent 50%)
            `,
          }}
        />
        <FadeIn className="relative mx-auto max-w-2xl text-center">
          <h2 className="text-balance text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Put Your Phone System Online.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Start with an SiteGlue Agent that works alongside your team—not instead of them.
          </p>
          <div className="mx-auto mt-8 max-w-xl text-left">
            <BuildAiPhoneSystemForm
              id="build-ai-phone-system-footer"
              location="final_cta"
              tone="dark"
              inputId="ai-phone-system-website-footer"
              className="mx-auto"
            />
          </div>
        </FadeIn>
      </HomeSection>
    </div>
  );
}
